import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TICKET_PRIORITIES } from "@/lib/tickets";
import { ArrowLeft, Shield } from "lucide-react";
import CcInput from "@/components/CcInput";

export default function TicketNew() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const internalMode = searchParams.get("internal") === "1" && !!user?.is_internal_use;
    const isStaff = ["agent", "super_agent", "admin", "superadmin"].includes(user?.role);
    const isSuperadmin = user?.role === "superadmin";
    const [form, setForm] = useState({ subject: "", description: "", priority: "medium", tags: "", group_id: "", requester_id: user?.user_id || "", company_id: "", cc_emails: [] });
    const [groups, setGroups] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [companies, setCompanies] = useState([]);  // empresas del cliente (lo que él ve via GET /companies)
    const [directory, setDirectory] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [appliedTemplateId, setAppliedTemplateId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [g, u, dir, comps, tpls] = await Promise.all([
                    api.get("/groups"),
                    isStaff ? api.get("/users", { params: { role: "customer", scope: "mine" } }) : Promise.resolve({ data: [] }),
                    api.get("/users/directory"),
                    api.get("/companies", { params: { scope: "mine", for_create: true } }),  // solo las empresas visibles al usuario; incluye las is_client para que un agente pueda levantar tickets de uso interno donde figura como cliente
                    api.get("/ticket-templates"),
                ]);
                setGroups(g.data);
                setCustomers(u.data);
                setDirectory(dir.data || []);
                setCompanies(comps.data || []);
                setTemplates(tpls.data || []);
                // Si el cliente tiene una sola empresa, preseleccionar
                if (!isStaff && Array.isArray(comps.data) && comps.data.length === 1) {
                    setForm((f) => ({ ...f, company_id: comps.data[0].company_id }));
                }
            } catch (_e) { /* ignore */ }
        })();
    }, [isStaff]);

    // Empresas a mostrar en el dropdown:
    //  - Cliente: las que recibió de /companies (sus empresas asignadas + POR ASIGNAR si la tiene)
    //  - Agente sin requester elegido: ninguna (deshabilitado)
    //  - Agente con requester elegido: las empresas de ese cliente específico
    const selectedCustomer = useMemo(() =>
        customers.find((c) => c.user_id === form.requester_id),
        [customers, form.requester_id]
    );

    const allowedCompanies = useMemo(() => {
        if (internalMode) {
            // En modo interno, solo empresas marcadas como Uso Interno.
            return companies.filter((c) => c.is_internal_use);
        }
        if (!isStaff) return companies;
        // Staff sees the companies they belong to (already scoped via ?scope=mine from /companies).
        // Superadmin gets full list.
        return companies;
    }, [isStaff, companies, internalMode]);

    // Templates visibles en el selector (filtra a internos si estamos en modo interno).
    const visibleTemplates = useMemo(() => {
        return internalMode ? templates.filter((t) => t.is_internal_use) : templates;
    }, [templates, internalMode]);

    // Auto-preseleccionar empresa cuando customer tiene una sola opción (no-staff)
    useEffect(() => {
        if (isStaff) return;
        if (allowedCompanies.length === 1) {
            setForm((f) => ({ ...f, company_id: allowedCompanies[0].company_id }));
        } else if (allowedCompanies.length === 0) {
            setForm((f) => ({ ...f, company_id: "" }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedCompanies, isStaff]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_id) {
            setError(isStaff ? "Selecciona la empresa y el cliente antes de crear el ticket." : "Selecciona la empresa del ticket.");
            return;
        }
        if (isStaff && !form.requester_id) {
            setError("Selecciona el cliente a nombre de quién se levanta el ticket.");
            return;
        }
        setLoading(true); setError("");
        try {
            const payload = {
                subject: form.subject,
                description: form.description,
                priority: form.priority,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                group_id: form.group_id || null,
                company_id: form.company_id,
                cc_emails: form.cc_emails || [],
            };
            if (isStaff && form.requester_id) payload.requester_id = form.requester_id;
            const { data } = await api.post("/tickets", payload);
            navigate(`/tickets/${data.ticket_id}`, { replace: true });
        } catch (err) { setError(formatApiError(err)); }
        finally { setLoading(false); }
    };

    // For staff: when an Empresa is preselected first, filter customers/super_customers
    // to ONLY those who belong to that company (assignments + primary + watched).
    const allowedCustomers = useMemo(() => {
        if (!isStaff) return customers;
        if (!form.company_id) return customers; // no filter yet
        return customers.filter((c) => {
            const ids = new Set([
                ...((c.company_assignments || []).map((a) => a.company_id)),
                c.company_id,
                ...(c.watched_company_ids || []),
            ].filter(Boolean));
            return ids.has(form.company_id);
        });
    }, [isStaff, customers, form.company_id]);

    // If the currently-selected requester no longer belongs to the chosen company, clear it.
    // Excepción: "yo mismo" (staff seleccionado como su propio requester) NO se desmarca,
    // porque el staff puede crear tickets para sí mismo sin pertenecer formalmente a la empresa.
    useEffect(() => {
        if (!isStaff || !form.requester_id || !form.company_id) return;
        if (form.requester_id === user?.user_id) return;
        if (!allowedCustomers.some((c) => c.user_id === form.requester_id)) {
            setForm((f) => ({ ...f, requester_id: "" }));
        }
    }, [form.company_id, allowedCustomers, form.requester_id, isStaff, user?.user_id]);

    const companyDisabled = !isStaff ? companies.length <= 1 : false;
    const companyHelp = !isStaff
        ? (companies.length === 1 ? "Tu única empresa asignada queda preseleccionada." : "Selecciona la empresa relacionada con este ticket.")
        : (form.company_id ? "Solo verás clientes asignados a esta empresa." : "Selecciona primero la empresa para ver los clientes asignados a ella.");

    const applyTemplate = (tplId) => {
        setAppliedTemplateId(tplId);
        if (!tplId) return;
        const t = templates.find((x) => x.template_id === tplId);
        if (!t) return;
        setForm((f) => ({
            ...f,
            subject: t.subject || f.subject,
            description: t.description || f.description,
            priority: t.priority || f.priority,
            tags: Array.isArray(t.tags) && t.tags.length ? t.tags.join(", ") : f.tags,
            group_id: t.group_id || f.group_id,
        }));
    };

    // Plantillas agrupadas por categoría (orden estable; las sin categoría al final)
    const groupedTemplates = useMemo(() => {
        const buckets = new Map();
        for (const t of visibleTemplates) {
            const key = t.category || "Sin categoría";
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(t);
        }
        return Array.from(buckets.entries()).sort(([a], [b]) => {
            if (a === "Sin categoría") return 1;
            if (b === "Sin categoría") return -1;
            return a.localeCompare(b);
        });
    }, [visibleTemplates]);

    // Auto-preseleccionar empresa interna si solo hay 1 (modo interno + staff)
    useEffect(() => {
        if (!internalMode) return;
        if (allowedCompanies.length === 1 && !form.company_id) {
            setForm((f) => ({ ...f, company_id: allowedCompanies[0].company_id }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalMode, allowedCompanies]);

    return (
        <div data-testid="ticket-new-page" style={{ maxWidth: 780 }}>
            <div className="page-head">
                <div>
                    <Link to={internalMode ? "/internal/dashboard" : "/tickets"} className="linklike" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        <ArrowLeft size={14} /> Volver {internalMode ? "a Mundo Interno" : "a tickets"}
                    </Link>
                    <h1 style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                        {internalMode && <Shield size={20} style={{ color: "var(--pill-warning-fg)" }} />}
                        {internalMode ? "Nuevo ticket interno" : "Nuevo ticket"}
                    </h1>
                    <p>
                        {internalMode
                            ? "Solo se muestran empresas y tipos de solicitud marcados como Uso Interno."
                            : "Describe tu solicitud con el mayor detalle posible."}
                    </p>
                </div>
            </div>

            {error && <div className="alert alert-error" data-testid="ticket-new-error">{error}</div>}

            <form onSubmit={submit} className="card card-padded row" style={{ gap: 16 }}>
                {visibleTemplates.length > 0 && (
                    <div className="field" data-testid="ticket-new-template-field">
                        <label>Tipo de solicitud (plantilla rápida)</label>
                        <select
                            className="select"
                            value={appliedTemplateId}
                            onChange={(e) => applyTemplate(e.target.value)}
                            data-testid="ticket-new-template"
                        >
                            <option value="">— Empezar desde cero —</option>
                            {groupedTemplates.map(([cat, items]) => (
                                <optgroup key={cat} label={cat}>
                                    {items.map((t) => (
                                        <option key={t.template_id} value={t.template_id}>{t.title}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <p className="text-muted text-xs" style={{ marginTop: 4 }}>
                            Selecciona una plantilla para rellenar asunto, descripción, prioridad y etiquetas. Puedes editar todo después.
                        </p>
                    </div>
                )}
                {isStaff && (
                    <div className="row row-2">
                        <div className="field">
                            <label>Empresa *</label>
                            <select
                                className="select"
                                value={form.company_id}
                                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                                required
                                disabled={companyDisabled}
                                data-testid="ticket-new-company"
                            >
                                <option value="">— Selecciona una empresa —</option>
                                {allowedCompanies.map((c) => (
                                    <option key={c.company_id} value={c.company_id}>
                                        {c.name}{c.is_placeholder ? " (POR ASIGNAR)" : ""}{c.is_confidential ? " · Confidencial" : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="text-muted text-xs" style={{ marginTop: 4 }}>{companyHelp}</p>
                        </div>
                        <div className="field">
                            <label>Crear en nombre de *</label>
                            <select className="select" value={form.requester_id} onChange={(e) => setForm({ ...form, requester_id: e.target.value })} disabled={!form.company_id && form.requester_id !== user?.user_id} data-testid="ticket-new-requester">
                                {user?.user_id && (
                                    <option value={user.user_id} data-testid="ticket-new-requester-self">
                                        Yo mismo ({user.name})
                                    </option>
                                )}
                                {!form.company_id && <option value="" disabled>Selecciona una empresa para elegir otro cliente</option>}
                                {form.company_id && allowedCustomers.length === 0 && <option value="" disabled>— Sin clientes en esta empresa —</option>}
                                {allowedCustomers
                                    .filter((c) => c.user_id !== user?.user_id)
                                    .map((c) => <option key={c.user_id} value={c.user_id}>{`${c.name} · ${c.email}`}</option>)}
                            </select>
                            <p className="text-muted text-xs" style={{ marginTop: 4 }}>
                                {form.requester_id === user?.user_id
                                    ? "El ticket quedará registrado a tu nombre como solicitante."
                                    : `Solo se muestran clientes asignados a la empresa elegida. El ticket quedará a su nombre y registrará que tú (${user?.name}) lo levantaste.`}
                            </p>
                        </div>
                    </div>
                )}
                <div className="field">
                    <label>Asunto *</label>
                    <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required maxLength={200} placeholder="Resumen breve del problema" data-testid="ticket-new-subject" />
                </div>
                <div className="field">
                    <label>Descripción *</label>
                    <textarea className="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={6} placeholder="Detalla el problema, pasos para reproducir, mensajes de error…" data-testid="ticket-new-description" />
                </div>
                <div className="field">
                    <label>Cc (correos a copiar)</label>
                    <CcInput
                        value={form.cc_emails}
                        onChange={(arr) => setForm({ ...form, cc_emails: arr })}
                        suggestions={directory}
                        placeholder="Empieza a escribir un nombre o correo…"
                        testid="ticket-new-cc"
                    />
                    <p className="text-muted text-xs" style={{ marginTop: 4 }}>Selecciona del directorio o teclea correos libres. Recibirán copia de las notificaciones por email asociadas a este ticket.</p>
                </div>
                <div className="row row-2">
                    <div className="field">
                        <label>Prioridad</label>
                        <select className="select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ticket-new-priority">
                            {TICKET_PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>Etiquetas (separadas por coma)</label>
                        <input className="input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="ej. acceso, urgente" data-testid="ticket-new-tags" />
                    </div>
                </div>
                {isStaff && (
                    <div className="field">
                        <label>Grupo / cola</label>
                        <select className="select" value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })} data-testid="ticket-new-group">
                            <option value="">— ninguno —</option>
                            {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                        </select>
                    </div>
                )}
                {!isStaff && (
                    <div className="field">
                        <label>Empresa *</label>
                        <select
                            className="select"
                            value={form.company_id}
                            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                            required
                            disabled={companyDisabled}
                            data-testid="ticket-new-company"
                        >
                            <option value="">— Selecciona una empresa —</option>
                            {allowedCompanies.map((c) => (
                                <option key={c.company_id} value={c.company_id}>
                                    {c.name}{c.is_placeholder ? " (POR ASIGNAR)" : ""}{c.is_confidential ? " · Confidencial" : ""}
                                </option>
                            ))}
                        </select>
                        <p className="text-muted text-xs" style={{ marginTop: 4 }}>{companyHelp}</p>
                    </div>
                )}

                <div className="flex gap-3" style={{ justifyContent: "flex-end" }}>
                    <button type="button" className="btn btn-ghost" onClick={() => navigate("/tickets")}>Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={loading} data-testid="ticket-new-submit">
                        {loading ? <span className="loader" /> : "Crear ticket"}
                    </button>
                </div>
            </form>
        </div>
    );
}
